/**
 * Centralized Type Definitions for Projects Admin
 *
 * All shared types should be defined here to ensure consistency
 * across the application. Import from '@/types' in components.
 */

// ============================================================================
// API Response Types
// ============================================================================

export interface ApiResponse<T = unknown> {
  success: boolean;
  data?: T;
  error?: ApiError;
  meta?: PaginationMeta;
  pagination?: PaginationMeta; // Alternative structure from backend
}

export interface ApiError {
  code: string;
  message: string;
  details?: Record<string, string[]>;
}

export interface PaginationMeta {
  page: number;
  limit: number;
  total: number;
  totalPages: number;
}

export interface PaginationParams {
  page?: number;
  limit?: number;
  search?: string;
  sortBy?: string;
  sortOrder?: 'asc' | 'desc';
}

// ============================================================================
// User & Auth Types
// ============================================================================

export type UserRole = 'SUPER_ADMIN' | 'PROJECT_ADMIN' | 'MODERATOR';

export type TeamRole = 'owner' | 'admin' | 'editor' | 'viewer';

export type Capability =
  | 'view_dashboard'
  | 'view_project'
  | 'edit_project'
  | 'manage_content'
  | 'manage_plugins'
  | 'manage_ai_training'
  | 'invite_members'
  | 'manage_billing'
  | 'delete_project';

export interface User {
  id: string;
  email: string;
  firstName: string;
  lastName: string;
  role: UserRole;
  teamRole?: TeamRole | null;
  teamId?: string | null;
  capabilities?: Capability[];
  status: 'ACTIVE' | 'INACTIVE' | 'SUSPENDED';
  createdAt: string;
  updatedAt: string;
  /** Quarterly rules notice (IT Rules 3(1)(c)); sent by /auth/me, /login, /register. */
  userRulesNotice?: UserRulesNoticeStatus;
}

export interface UserRulesNoticeStatus {
  version: string;
  due: boolean;
  acknowledgedVersion: string | null;
  acknowledgedAt: string | null;
  nextDueAt: string | null;
}

export type UserRulesNoticeLanguage = 'en' | 'hi';

/** Text of the notice in one language, as served by GET /user-rules-notice. */
export interface UserRulesNoticeText {
  title: string;
  lead: string;
  rulesHeading: string;
  rulesIntro: string;
  rules: string[];
  consequencesHeading: string;
  consequences: string;
  aiHeading: string;
  ai: string;
  termsLink: string;
  grievanceLink: string;
  translationNote: string | null;
  acknowledge: string;
  later: string;
  bannerText: string;
  bannerAction: string;
  languageLabel: string;
}

export interface UserRulesNoticeContent {
  version: string;
  brand: string;
  links: { terms: string; grievance: string; grievanceHi: string };
  content: Record<UserRulesNoticeLanguage, UserRulesNoticeText>;
}

/** Check if the current user has a specific capability */
export function can(user: User | null | undefined, capability: Capability): boolean {
  if (!user) return false;
  if (user.role === 'SUPER_ADMIN') return true;
  return user.capabilities?.includes(capability) ?? false;
}

export interface AuthTokens {
  accessToken: string;
  refreshToken: string;
}

export interface LoginCredentials {
  email: string;
  password: string;
}

export interface RegisterData {
  email: string;
  password: string;
  firstName: string;
  lastName: string;
}

export interface CreateUserData {
  email: string;
  firstName: string;
  lastName: string;
  role: UserRole;
}

export interface UpdateUserData {
  firstName?: string;
  lastName?: string;
  email?: string;
  role?: UserRole;
  status?: 'ACTIVE' | 'INACTIVE' | 'SUSPENDED';
}

/**
 * Blast radius of deleting a user, from GET /users/:id/deletion-impact.
 *
 * Teams are split by whether anyone else is actively working in them:
 *  - `soleOwnerTeams` are deleted outright with the account (repos archived,
 *    never deleted — platform policy).
 *  - `sharedTeams` must be handed to a new owner first; `candidates` is what
 *    the transfer dialog offers.
 */
export interface UserDeletionImpact {
  soleOwnerTeams: Array<{ teamId: string; teamName?: string; projectCount: number }>;
  sharedTeams: Array<{
    teamId: string;
    teamName?: string;
    projectCount: number;
    candidates: Array<{ userId: string; email: string; name: string; role: string }>;
  }>;
  requiresOwnershipTransfer: boolean;
}

export interface AssignProjectData {
  projectId: string;
  role: UserRole;
}

// ============================================================================
// Project Types
// ============================================================================

export type ProjectStatus = 'ACTIVE' | 'INACTIVE' | 'SUSPENDED' | 'ARCHIVED';

export interface Project {
  id: string;
  teamId?: string; // The team this project belongs to (one project → one team)
  name: string;
  slug: string;
  description?: string;
  industry?: string; // Business industry/niche for AI features
  domain?: string;
  /** Connected custom hostnames for this project. First entry is the canonical
   *  customer-facing URL when present. */
  domains?: string[];
  localDevUrl?: string; // Local development URL (e.g., http://localhost:8080)
  apiKey?: string; // Only returned on project creation
  apiKeyPrefix?: string; // Partial key for identification (e.g., "ak_abc123...")
  status: ProjectStatus;
  /** Present when status === 'SUSPENDED' — who/when/why (superadmin action). */
  suspension?: {
    suspendedAt: string;
    suspendedBy: string;
    suspendedByEmail?: string;
    reason?: string;
    previousStatus: ProjectStatus;
  } | null;
  /** Present when status === 'ARCHIVED' — who/when/why + what was torn down. */
  archival?: {
    archivedAt: string;
    archivedBy: string;
    archivedByEmail?: string;
    reason?: string;
    previousStatus: ProjectStatus;
    /** Teardown receipts (informational) — what archive actually removed. */
    teardown?: {
      customHostnamesRemoved?: number;
      workerScriptsDeleted?: string[];
      githubRepoArchived?: boolean;
      errors?: string[];
    };
  } | null;
  settings: ProjectSettings;
  features: ProjectFeatures;
  installedPlugins?: string[]; // Plugin IDs (legacy — prefer pluginCount)
  /** Server-computed count of ENABLED pluginConfigs rows for this project. */
  pluginCount?: number;
  allowedDomains?: string[]; // For CORS
  /** Direct Cloudflare Worker script name (e.g. `site-acme-7f3a`). Always
   *  populated by the backend for Worker-dispatched projects; the SPA gates
   *  display to SUPER_ADMIN / ghost-login. */
  workerScriptName?: string | null;
  /** `https://<workerScriptName>.snowy-cherry-cd2c.workers.dev`. Internal-facing. */
  workerUrl?: string | null;
  /** `https://<slug>.sites.tyashin.com` — every project's free Tyashin subdomain.
   *  Used as the public hyperlink fallback when no custom domain is connected. */
  sitesUrl?: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface ProjectSettings {
  allowedOrigins: string[];
  rateLimits: {
    enabled: boolean;
    maxRequests: number;
    windowMs: number;
  };
  webhooks: {
    enabled: boolean;
    url?: string;
    secret?: string;
  };
}

export interface ProjectFeatures {
  newsletter: boolean;
  contact: boolean;
  consent: boolean;
  analytics: boolean;
}

export interface CreateProjectData {
  name: string;
  slug?: string;
  description?: string;
  industry?: string;
  domain?: string;
  localDevUrl?: string;
}

export interface LegalPage {
  enabled: boolean;
  title: string;
  content: string;
}

export interface LegalPages {
  termsAndConditions?: LegalPage;
  privacyPolicy?: LegalPage;
  returnPolicy?: LegalPage;
  copyrightText?: string;
  showFooterBar?: boolean;
}

export interface UpdateProjectData {
  name?: string;
  description?: string;
  industry?: string;
  domain?: string;
  localDevUrl?: string;
  status?: ProjectStatus;
  settings?: Partial<ProjectSettings>;
  features?: Partial<ProjectFeatures>;
  legalPages?: Partial<LegalPages>;
}

export interface ConnectProjectData {
  apiKey: string;
}

// ============================================================================
// Plugin Types
// ============================================================================

export type PluginCategory = 'ANALYTICS' | 'MARKETING' | 'COMMUNICATION' | 'SECURITY' | 'OTHER';

export interface Plugin {
  id: string;
  name: string;
  slug: string;
  description: string;
  category: PluginCategory;
  version: string;
  icon?: string;
  configSchema?: Record<string, PluginConfigField>;
  isEnabled: boolean;
}

export interface PluginConfigField {
  type: 'string' | 'number' | 'boolean' | 'select' | 'multiselect';
  label: string;
  description?: string;
  required?: boolean;
  default?: unknown;
  options?: { value: string; label: string }[];
}

export interface ProjectPlugin {
  id: string;
  pluginId: string;
  projectId: string;
  plugin: Plugin;
  isEnabled: boolean;
  config: Record<string, unknown>;
  installedAt: string;
}

export interface InstallPluginData {
  pluginId: string;
  config?: Record<string, unknown>;
}

export interface UpdatePluginConfigData {
  config: Record<string, unknown>;
  isEnabled?: boolean;
}

// ============================================================================
// Newsletter Types
// ============================================================================

export interface NewsletterSubscriber {
  id: string;
  projectId: string;
  email: string;
  name?: string;
  status: 'ACTIVE' | 'UNSUBSCRIBED' | 'BOUNCED';
  source?: string;
  metadata?: Record<string, unknown>;
  subscribedAt: string;
  unsubscribedAt?: string;
}

export interface NewsletterStats {
  total: number;
  active: number;
  unsubscribed: number;
  bounced: number;
  confirmed?: number; // For double opt-in systems
  pending?: number; // Pending confirmation
  lastWeek: number;
  lastMonth: number;
}

export interface NewsletterQueryParams extends PaginationParams {
  status?: 'ACTIVE' | 'UNSUBSCRIBED' | 'BOUNCED';
  dateFrom?: string;
  dateTo?: string;
}

// ============================================================================
// Contact Types
// ============================================================================

// D1 stores lowercase status values — the API returns and expects them
// lowercase. (Previously these were uppercase and silently broke the status
// filter, the "new" highlight, and status PATCH requests.)
export type ContactStatus = 'new' | 'read' | 'replied' | 'archived' | 'spam';

export interface ContactSubmission {
  id: string;
  projectId: string;
  name: string;
  email: string;
  phone?: string;
  subject?: string;
  message: string;
  status: ContactStatus;
  company?: string;
  category?: string;
  budget?: string;
  source?: string;
  assignedTo?: string | null;
  metadata?: Record<string, unknown>;
  submittedAt: string;
  createdAt?: string; // Alias for submittedAt in some backends
  readAt?: string;
  repliedAt?: string;
}

export interface ContactStats {
  total: number;
  new: number;
  read: number;
  replied: number;
  archived: number;
  spam: number;
  lastWeek: number;
  lastMonth: number;
}

export interface ContactQueryParams extends PaginationParams {
  status?: ContactStatus;
  dateFrom?: string;
  dateTo?: string;
}

export interface UpdateContactData {
  status?: ContactStatus;
}

export interface BulkContactActionData {
  ids: string[];
  action: 'markRead' | 'markReplied' | 'archive' | 'delete' | 'markSpam';
}

// ============================================================================
// Consent Types
// ============================================================================

export type ConsentCategory = 'NECESSARY' | 'ANALYTICS' | 'MARKETING' | 'PREFERENCES' | 'SOCIAL';

export interface ConsentSettings {
  id: string;
  projectId: string;
  enabled: boolean;
  banner: ConsentBannerSettings;
  categories: ConsentCategoryConfig[];
  styling: ConsentStylingConfig;
}

export interface ConsentBannerSettings {
  title: string;
  description: string;
  acceptAllText: string;
  rejectAllText: string;
  customizeText: string;
  privacyPolicyUrl?: string;
  cookiePolicyUrl?: string;
}

export interface ConsentCategoryConfig {
  id: ConsentCategory;
  name: string;
  description: string;
  required: boolean;
  defaultEnabled: boolean;
}

export interface ConsentStylingConfig {
  position: 'bottom' | 'top' | 'bottom-left' | 'bottom-right' | 'center';
  theme: 'light' | 'dark' | 'auto';
  primaryColor?: string;
  showLogo: boolean;
}

export interface ConsentRecord {
  id: string;
  projectId: string;
  visitorId: string;
  categories: Record<ConsentCategory, boolean>;
  ipAddress?: string;
  userAgent?: string;
  timestamp: string;
}

export interface ConsentAnalytics {
  totalRecords: number;
  total?: number; // Alias for totalRecords
  acceptAll: number;
  rejectAll: number;
  customized: number;
  acceptanceRate?: number; // 0-1 percentage
  categoryBreakdown: Record<ConsentCategory, { accepted: number; rejected: number }>;
  byCategory?: Record<string, { accepted: number; rejected: number }>; // Alternative structure
}

export interface ConsentQueryParams extends PaginationParams {
  dateFrom?: string;
  dateTo?: string;
}

export interface UpdateConsentSettingsData {
  enabled?: boolean;
  banner?: Partial<ConsentBannerSettings>;
  categories?: ConsentCategoryConfig[];
  styling?: Partial<ConsentStylingConfig>;
}

// ============================================================================
// Analytics Types
// ============================================================================

export interface AnalyticsSummary {
  pageViews: number;
  uniqueVisitors: number;
  avgSessionDuration: number;
  bounceRate: number;
  topPages: { path: string; views: number }[];
  trafficSources: { source: string; visits: number }[];
  deviceBreakdown: { device: string; percentage: number }[];
}

export interface AnalyticsTimeSeriesData {
  date: string;
  pageViews: number;
  visitors: number;
}

// ============================================================================
// Blog / CMS Types
// ============================================================================

export type BlogPostStatus = 'draft' | 'published' | 'scheduled' | 'archived';

export interface BlogPost {
  id: string;
  projectId: string;
  title: string;
  slug: string;
  excerpt?: string;
  content: string;
  contentFormat: 'html' | 'markdown';
  featuredImage?: string;
  status: BlogPostStatus;
  authorId: string;
  authorName?: string;
  categoryId?: string;
  tags: string[];
  seo?: {
    metaTitle?: string;
    metaDescription?: string;
    canonicalUrl?: string;
    ogImage?: string;
  };
  publishedAt?: string;
  scheduledAt?: string;
  viewCount: number;
  createdAt: string;
  updatedAt: string;
}

export interface BlogSuggestion {
  id: string;
  status: 'pending' | 'generated' | 'dismissed';
  title: string;
  angle?: string;
  rationale?: string;
  keywords: string[];
  signals: Array<{ term: string; source: string; url?: string }>;
  productToFeature?: string;
  tone?: string;
  weekKey: string;
  generatedPostId?: string;
  generatedAt?: string;
  createdAt: string;
  /**
   * 'trend' (weekly research), 'topic-engine', 'aeo' (fed by AI Answer
   * Visibility), or 'manual' (the admin's own topic, "Add topic").
   */
  source?: 'trend' | 'topic-engine' | 'aeo' | 'manual';
  priority?: number;
  /** AI Answer Visibility feed: the buyer question this post must answer. */
  aeo?: { questionId: string; question: string };
  engine?: {
    clusterId: string;
    clusterName: string;
    nodeId?: string;
    archetype: string;
    targetQuery: string;
    intent: string;
    scores?: { fit: number; winnability: number; demand: number; gap: number; priority: number };
    competingTitles?: string[];
    internalLinks?: string[];
  };
}

export type TopicNodeStatus = 'planned' | 'queued' | 'written' | 'rewrite' | 'dismissed';

export interface TopicNode {
  id: string;
  archetype: string;
  title: string;
  targetQuery: string;
  intent: string;
  rationale?: string;
  productToFeature?: string;
  status: TopicNodeStatus;
  postId?: string;
  slug?: string;
  suggestionId?: string;
  scores?: {
    fit: number;
    winnability: number;
    demand: number;
    gap: number;
    priority: number;
    serpNote?: string;
    competingTitles?: string[];
  };
  metrics?: { weekKey: string; impressions: number; clicks: number; position: number };
}

export interface TopicCluster {
  id: string;
  name: string;
  terms: string[];
  pillar: TopicNode;
  spokes: TopicNode[];
}

export interface TopicMapResponse {
  map: {
    id: string;
    version: number;
    businessType: string;
    generatedAt: string;
    updatedAt: string;
    lastRunAt?: string;
    lastRunSummary?: string;
    clusters: TopicCluster[];
    overlaps: Array<{ postIds: string[]; titles: string[]; reason: string }>;
  } | null;
  readiness: {
    ok: boolean;
    reason?: string;
    businessType?: string;
    products: number;
    posts: number;
  };
  archetypes: Array<{ id: string; label: string; intent: string }>;
}

export interface IndustryProfile {
  industry: string;
  productCategories?: string[];
  targetAudience?: string;
  keywords?: string[];
  source: string;
  editedByUser?: boolean;
  resolvedAt?: string;
}

export interface BlogQuotaMeter {
  used: number;
  limit: number;
  remaining: number;
}

/** Body of POST /blog/projects/:id/suggestions (lib/ai/blog/manual-topic.ts). */
export interface ManualBlogTopicInput {
  title: string;
  angle?: string;
  keywords: string[];
  rationale?: string;
  /** Sources the post must cite; `term` is the claim the source backs. */
  signals?: Array<{ term: string; source: string; url: string }>;
  productToFeature?: string;
  tone?: string;
}

export interface BlogSuggestionsResponse {
  suggestions: BlogSuggestion[];
  usage: {
    plan: string;
    suggestions: BlogQuotaMeter;
    published: BlogQuotaMeter;
  };
}

export interface BlogCategory {
  id: string;
  projectId: string;
  name: string;
  slug: string;
  description?: string;
  parentId?: string;
  order: number;
  createdAt: string;
  updatedAt: string;
  /** Posts currently assigned. Present on the list endpoint only. */
  postCount?: number;
}

export interface BlogTag {
  id: string;
  projectId: string;
  name: string;
  slug: string;
  createdAt: string;
  updatedAt: string;
}

export interface CreateBlogPostData {
  title: string;
  slug?: string;
  excerpt?: string;
  content: string;
  contentFormat?: 'html' | 'markdown';
  featuredImage?: string;
  status?: BlogPostStatus;
  categoryId?: string;
  tags?: string[];
  seo?: {
    metaTitle?: string;
    metaDescription?: string;
    canonicalUrl?: string;
    ogImage?: string;
  };
  scheduledAt?: string;
}

export type UpdateBlogPostData = Partial<CreateBlogPostData>;

export interface CreateBlogCategoryData {
  name: string;
  slug?: string;
  description?: string;
  parentId?: string;
  order?: number;
}

export interface CreateBlogTagData {
  name: string;
  slug?: string;
}

// ============================================================================
// Plugin Manifest v2 Types
// ============================================================================

export type PluginType = 'builtin' | 'aapastech-service' | 'oauth-integration' | 'third-party';

// ── Integration Contract (per docs/PLUGIN-CONTRACT.md) ────────────────────
export type ConnectionStrategy = 'internal' | 'oauth' | 'api-key' | 'none';
export type EmbedSupport = 'iframe' | 'external' | 'none';

export interface PluginEndpoints {
  install?: string;
  uninstall?: string;
  admin?: string;
  usageSummary?: string;
  health?: string;
  oauthAuthorize?: string;
  oauthTokenExchange?: string;
}

export interface PluginPricing {
  model: 'free' | 'included' | 'freemium' | 'paid';
  displayPrice?: string;
  billingManagedBy: 'tyashin' | 'vendor';
}

export interface PluginLinks {
  dashboard?: string;
  docs?: string;
  support?: string;
  website?: string;
  privacy?: string;
}

export interface ConfigSection {
  id: string;
  title: string;
  description?: string;
  fields: string[];
}

export type AdminTabType = 'overview' | 'settings' | 'admin' | 'billing' | 'custom';

export interface AdminTab {
  id: string;
  label: string;
  type: AdminTabType;
  redirectPath?: string;
  icon?: string;
}

export type OverviewWidgetType = 'stat' | 'chart' | 'link-card';

export interface OverviewWidget {
  type: OverviewWidgetType;
  title: string;
  metricKey?: string;
  unit?: string;
  href?: string;
  description?: string;
  cta?: string;
  icon?: string;
}

export interface AdminPageConfig {
  tabs: AdminTab[];
  showHealthStatus?: boolean;
  showUsageMetrics?: boolean;
  overviewWidgets?: OverviewWidget[];
}

export interface DataPolicy {
  onUninstall: 'retain' | 'archive' | 'delete';
  retentionDays?: number;
  gdprRelevant: boolean;
}

export type HealthStatus = 'healthy' | 'degraded' | 'error' | 'unconfigured' | 'unknown';

export interface HealthCheckResult {
  status: HealthStatus;
  message: string;
  lastChecked: string;
  strategy: string;
  durationMs?: number;
}

export interface PluginManifest {
  id: string;
  name: string;
  description: string;
  category: string;
  version: string;
  icon?: string;
  pluginType: PluginType;
  adapterType?: string;
  authType: string;
  credentialFields: Array<{
    key: string;
    label: string;
    type: string;
    required: boolean;
    placeholder?: string;
    helpText?: string;
  }>;
  configSchema: Record<string, any>;
  defaultConfig: Record<string, any>;
  configSections?: ConfigSection[];
  features: string[];
  requiresSetup: boolean;
  pricing: PluginPricing;
  links: PluginLinks;
  adminPage: AdminPageConfig;
  // Integration contract (Phase 2)
  connectionStrategy?: ConnectionStrategy;
  endpoints?: PluginEndpoints;
  embedSupport?: EmbedSupport;
  dataPolicy: DataPolicy;
  dependencies?: string[];
  conflicts?: string[];
  /** Hidden from the marketplace catalog; installed detail pages still resolve */
  hidden?: boolean;
}

export interface InstalledPluginInfo {
  isEnabled: boolean;
  config: Record<string, any>;
  installedAt: string;
  installedBy: string;
}

export interface PluginUsageData {
  plan: string;
  period: string;
  current: { conversations: number; aiMessages: number; messages: number };
  limits: { conversations: number; aiMessages: number };
  allowed: boolean;
  upsell: {
    reason: string;
    percentUsed: number;
    message: string;
    severity: 'warning' | 'critical';
  } | null;
}

export interface PluginSubscriptionData {
  pluginId: string;
  plan: string;
  price: number;
  currency: string;
  status: 'active' | 'cancelled' | 'past_due';
  subscribedAt: string;
  currentPeriodEnd: string;
  cancelAtPeriodEnd?: boolean;
  paymentMethod: 'credits' | 'gateway' | 'free';
}

export interface PluginManifestResponse {
  manifest: PluginManifest;
  installed: InstalledPluginInfo | null;
  subscription?: PluginSubscriptionData | null;
  usage?: PluginUsageData | null;
}

// ============================================================================
// DNS Migration Types
// ============================================================================

export type DnsMigrationState =
  | 'idle'
  | 'token_validated'
  | 'zone_ready'
  | 'records_imported'
  | 'tyashin_records_added'
  | 'waiting_for_ns'
  | 'zone_activating'
  | 'hostname_registering'
  | 'done'
  | 'failed'
  | 'aborted';

export interface DnsMigration {
  _id: string;
  projectId: string;
  teamId: string;
  apex: string;
  hostnameMode: 'apex' | 'www' | 'both';
  state: DnsMigrationState;
  lastError?: string;
  stateHistory: { state: DnsMigrationState; at: string; note?: string }[];
  cfAuth: {
    kind: 'token' | 'oauth';
    accountId: string;
    accountName?: string;
    tokenHint?: string;
  };
  zone?: {
    id: string;
    name: string;
    status: string;
    nameServers: string[];
    originalNameServers?: string[];
    autoImported: boolean;
    importedRecordCount?: number;
  };
  tyashinRecords?: {
    id: string;
    type: string;
    name: string;
    content: string;
    proxied?: boolean;
  }[];
  nsProgress?: {
    lastCheckedAt: string;
    registrarNameServers: string[];
    matched: number;
    expected: number;
  };
  createdAt: string;
  updatedAt: string;
  completedAt?: string;
  abortedAt?: string;
}

// ============================================================================
// Shipping Zones (v2 — country → region → postal-prefix)
// ============================================================================

/**
 * Shipping zone shape mirrored from backend (src/ecommerce/types.ts).
 * Keep in sync on schema changes.
 */
export interface ShippingZone {
  id: string;
  name: string;
  priority: number; // lower = checked first
  enabled: boolean;
  match: {
    countries: string[]; // ISO 3166-1 alpha-2, uppercase
    regions?: string[]; // ISO 3166-2 subdivision codes
    postalPrefixes?: string[]; // "110" matches "110019"; case-insensitive
  };
  rate: number; // smallest currency unit (cents/paise)
  freeAbove?: number; // threshold above which shipping becomes free
  estimatedDays: string; // free-form display label
}

// ============================================================================
// Utility Types
// ============================================================================

export type Nullable<T> = T | null;

export type DeepPartial<T> = {
  [P in keyof T]?: T[P] extends object ? DeepPartial<T[P]> : T[P];
};

/**
 * Extract the data type from an ApiResponse
 */
export type ExtractData<T> = T extends ApiResponse<infer U> ? U : never;

// ── Google Tag: "Connect with Google" ───────────────────────────────────────

export interface GoogleTagConnection {
  email: string | null;
  accountName: string;
  propertyId: string;
  propertyName: string;
  measurementId: string;
  source: 'picked' | 'created';
  connectedAt: string;
}

export interface GoogleTagConnectStatus {
  /** Platform has a Google OAuth client configured → Connect button shown */
  available: boolean;
  /** Google sign-in done, property not chosen yet → picker shown */
  pendingPick: boolean;
  connection: GoogleTagConnection | null;
  suggested: {
    displayName: string;
    websiteUrl: string | null;
    timeZone: string;
    currencyCode: string;
  };
}
